import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
  TypographyProps,
} from '@mui/material';

import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import { msToMinutesSeconds } from '../../../../helper';

import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../components/V2/Table/type1/TableHeadCustom';
import SubmissionTableRow from './components/SubmissionTableRow';
import {
  getScoreColor,
  getScoreStatus,
  getTimeIcon,
} from '../DetailedOverview/components/StudentPerformanceRow';

import {
  getSubmissionsV2State,
  IStudentPerformanceData,
} from '../../../../store/V2/SubmissionsSlice';

const typoProps: TypographyProps = {
  fontSize: '14px',
  color: TP_COLORS.gray.darken,
};

type TSelectOption = {
  label: string;
  value: string;
};

const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'topic',
    label: 'Topic',
    sortable: false,
  },
  {
    id: 'lastSubmission',
    label: 'Last Submission',
    sortable: false,
    minWidth: 120,
  },
  {
    id: 'score',
    label: 'Score (Avg)',
    sortable: false,
  },
  {
    id: 'timeTaken',
    label: 'Time taken (Avg)',
    sortable: false,
    minWidth: 80,
  },
  {
    id: 'action',
    label: '',
    sortable: false,
  },
];

interface IStudentDetailedOverviewProps {
  selectedStudent: TSelectOption;
}

const StudentDetailedOverview: React.FC<IStudentDetailedOverviewProps> = ({
  selectedStudent,
}) => {
  // Store
  const { groupedStudentsSubmissions, courseSubmissionsData } = useAppSelector(
    getSubmissionsV2State
  );

  // State
  const tableData = useTable(defaultTableData);
  const [
    selectedStudentData,
    setSelectedStudentData,
  ] = useState<IStudentPerformanceData | null>(null);

  const courseAverageScore = courseSubmissionsData?.totalAverageScore || 0;
  const courseAverageTime = courseSubmissionsData?.totalAverageTime || 0;
  const timeDif = courseAverageTime - (selectedStudentData?.averageTime || 0);

  useEffect(() => {
    if (groupedStudentsSubmissions && groupedStudentsSubmissions) {
      const findDetailedData = groupedStudentsSubmissions.find(
        (item) => item._id === selectedStudent.value
      );
      if (findDetailedData) {
        setSelectedStudentData(findDetailedData);
      }
    }
  }, [groupedStudentsSubmissions, selectedStudent]);

  if (selectedStudentData) {
    const sortedTopics = [...selectedStudentData.topics].sort(
      (a, b) => (a.sortingOrder ?? 0) - (b.sortingOrder ?? 0)
    );

    return (
      <div>
        <Grid container padding="18px" spacing={2}>
          <Grid item xs={12} sm={6} md={8}>
            <Box display="flex" gap="10px" alignItems="center">
              <Avatar alt="Remy Sharp" src={selectedStudentData.avatar || ''} />
              <Typography {...typoProps}>
                {selectedStudentData.firstname || ''}{' '}
                {selectedStudentData.lastname || ''}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Stack>
              <Typography {...typoProps}>
                {selectedStudentData.averageScore}/20
              </Typography>
              <Typography
                fontSize="12px"
                color={getScoreColor(
                  selectedStudentData.averageScore,
                  courseAverageScore
                )}
              >
                {getScoreStatus(
                  selectedStudentData.averageScore,
                  courseAverageScore
                )}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Stack>
              <Typography {...typoProps}>
                {msToMinutesSeconds(selectedStudentData.averageTime)}
              </Typography>
              {timeDif !== 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  color={getScoreColor(
                    courseAverageTime,
                    selectedStudentData.averageTime
                  )}
                >
                  {getTimeIcon(timeDif)}
                  <Typography fontSize="12px" marginLeft="2px">
                    {msToMinutesSeconds(Math.abs(timeDif))}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
        <Box sx={{ padding: '16px 20px', backgroundColor: '#FCFCFC ' }}>
          <TableContainer
            sx={{
              position: 'relative',
            }}
          >
            <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
              <TableHeadCustom
                order={tableData.order}
                orderBy={tableData.orderBy}
                headLabels={tableHeaderLabels}
                rowCount={0}
                numSelected={tableData.selected.length}
              />

              <TableBody sx={{ backgroundColor: 'white' }}>
                {sortedTopics.map((row, index) => (
                  <SubmissionTableRow
                    key={index}
                    rowData={row}
                    studentId={selectedStudent.value}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>
    );
  }

  return null;
};

export default StudentDetailedOverview;
